import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../../../services/api';
import { ApprovalConfiguration } from '../../../../types/workflow';
import MultiSelect from '../../../../components/MultiSelect';

interface ApprovalConfigProps {
  configuration: ApprovalConfiguration;
  onChange: (config: ApprovalConfiguration) => void;
  isReadOnly: boolean;
}

interface Group {
  id: string;
  name: string;
  description?: string;
  level?: number;
}

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
}

const ApprovalConfig: React.FC<ApprovalConfigProps> = ({
  configuration,
  onChange,
  isReadOnly,
}) => {
  // Use configuration directly for approverType to avoid sync issues
  const approverType = configuration.approverType || 'group';

  // Fetch groups
  const { data: groupsData, isLoading: groupsLoading } = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
      try {
        const response = await api.get('/groups?limit=100');
        console.log('Groups API response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error fetching groups:', error);
        return null;
      }
    },
  });

  // Fetch users
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      try {
        const response = await api.get('/users?limit=100');
        console.log('Users API response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error fetching users:', error);
        return null;
      }
    },
  });

  // Process groups data
  const groups: Group[] = React.useMemo(() => {
    if (!groupsData) return [];
    
    // Handle different response formats
    if (groupsData.groups && Array.isArray(groupsData.groups)) {
      return groupsData.groups;
    }
    if (Array.isArray(groupsData)) {
      return groupsData;
    }
    
    return [];
  }, [groupsData]);

  // Process users data and filter out super admin
  const users: User[] = React.useMemo(() => {
    if (!usersData) return [];
    
    let userList: User[] = [];
    
    // Handle different response formats
    if (usersData.users && Array.isArray(usersData.users)) {
      userList = usersData.users;
    } else if (Array.isArray(usersData)) {
      userList = usersData;
    }
    
    // Filter out super admin users
    return userList.filter(user => (user.role as any)?.name !== 'super_admin');
  }, [usersData]);

  // Available roles (excluding super_admin)
  const roles = [
    { value: 'admin', label: 'Admin', description: 'System administrators' },
    { value: 'manager', label: 'Manager', description: 'Team managers and supervisors' },
    { value: 'agent', label: 'Agent', description: 'Support agents and technicians' },
    { value: 'user', label: 'User', description: 'Regular users' },
  ];

  // Sync approvers and escalation group with configuration changes
  const selectedApprovers = configuration.approvers || [];
  const selectedEscalationGroup = configuration.escalationGroup || '';

  const updateConfig = (field: keyof ApprovalConfiguration, value: any) => {
    onChange({
      ...configuration,
      [field]: value,
    });
  };

  const handleApproverTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as 'user' | 'group' | 'role' | 'dynamic';
    // Update both approverType and clear approvers in a single update
    onChange({
      ...configuration,
      approverType: newType,
      approvers: []
    });
  };

  const handleApproversChange = (values: string[]) => {
    updateConfig('approvers', values);
  };

  const handleEscalationGroupChange = (values: string[]) => {
    const value = values[0] || '';
    updateConfig('escalationGroup', value);
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const values = e.target.value.split(',').map(v => v.trim()).filter(Boolean);
    updateConfig('approvers', values);
  };

  return (
    <div className="space-y-4">
      {/* Approver Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Approver Type
        </label>
        <select
          value={approverType}
          onChange={handleApproverTypeChange}
          disabled={isReadOnly}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
        >
          <option value="user">Specific User</option>
          <option value="group">Group</option>
          <option value="role">Role</option>
          <option value="dynamic">Dynamic (from context)</option>
        </select>
      </div>

      {/* Approvers Selection Based on Type */}
      {approverType === 'group' && (
        <div>
          {groups.length > 0 ? (
            <MultiSelect
              label="Select Approval Groups"
              options={groups.map(group => ({
                value: group.id,
                label: group.name,
                description: group.level ? `Level ${group.level}${group.description ? ' - ' + group.description : ''}` : group.description,
              }))}
              value={selectedApprovers}
              onChange={handleApproversChange}
              placeholder="Select groups for approval..."
              disabled={isReadOnly}
              loading={groupsLoading}
              helpText="Select one or more groups that need to approve"
            />
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Approval Groups
              </label>
              {groupsLoading ? (
                <div className="mt-1 p-2 text-sm text-gray-500 bg-gray-50 rounded">
                  Loading groups...
                </div>
              ) : (
                <input
                  type="text"
                  value={selectedApprovers.join(', ')}
                  onChange={handleTextInputChange}
                  disabled={isReadOnly}
                  placeholder="Enter group names or IDs (comma-separated)"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
                />
              )}
              <p className="mt-1 text-xs text-gray-500">
                {groupsLoading ? 'Groups are being loaded...' : 'Enter group names or IDs manually, separated by commas'}
              </p>
            </div>
          )}
        </div>
      )}

      {approverType === 'user' && (
        <div>
          {users.length > 0 ? (
            <MultiSelect
              label="Select Approvers"
              options={users.map(user => ({
                value: user.id,
                label: user.firstName && user.lastName 
                  ? `${user.firstName} ${user.lastName}`
                  : user.email,
                description: user.email,
              }))}
              value={selectedApprovers}
              onChange={handleApproversChange}
              placeholder="Select users for approval..."
              disabled={isReadOnly}
              loading={usersLoading}
              helpText="Select one or more users who need to approve (Super Admins excluded)"
            />
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Approvers
              </label>
              {usersLoading ? (
                <div className="mt-1 p-2 text-sm text-gray-500 bg-gray-50 rounded">
                  Loading users...
                </div>
              ) : (
                <input
                  type="text"
                  value={selectedApprovers.join(', ')}
                  onChange={handleTextInputChange}
                  disabled={isReadOnly}
                  placeholder="Enter user emails or IDs (comma-separated)"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
                />
              )}
              <p className="mt-1 text-xs text-gray-500">
                {usersLoading ? 'Users are being loaded...' : 'Enter user emails or IDs manually, separated by commas'}
              </p>
            </div>
          )}
        </div>
      )}

      {approverType === 'role' && (
        <MultiSelect
          label="Select Roles"
          options={roles}
          value={selectedApprovers}
          onChange={handleApproversChange}
          placeholder="Select roles for approval..."
          disabled={isReadOnly}
          helpText="Select one or more roles that need to approve"
        />
      )}

      {approverType === 'dynamic' && (
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Dynamic Approver Expression
          </label>
          <input
            type="text"
            value={selectedApprovers[0] || ''}
            onChange={(e) => {
              const value = e.target.value;
              updateConfig('approvers', value ? [value] : []);
            }}
            disabled={isReadOnly}
            placeholder="e.g., $\{requester.manager}, $\{item.owner}"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
          />
          <p className="mt-1 text-xs text-gray-500">
            Use workflow variables to dynamically determine approver
          </p>
        </div>
      )}

      {/* Unanimous Approval Checkbox */}
      <div>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={configuration.unanimous || false}
            onChange={(e) => updateConfig('unanimous', e.target.checked)}
            disabled={isReadOnly}
            className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
          />
          <span className="ml-2 text-sm text-gray-700">
            Require unanimous approval
          </span>
        </label>
        <p className="mt-1 text-xs text-gray-500 ml-6">
          All approvers must approve for the workflow to continue
        </p>
      </div>

      {/* Timeout Configuration */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Timeout (hours)
        </label>
        <input
          type="number"
          value={configuration.timeout || ''}
          onChange={(e) => updateConfig('timeout', parseInt(e.target.value) || undefined)}
          disabled={isReadOnly}
          placeholder="Leave empty for no timeout"
          min="1"
          max="720"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
        />
        <p className="mt-1 text-xs text-gray-500">
          Maximum time to wait for approval (1-720 hours)
        </p>
      </div>

      {/* Escalation Group */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Escalation Group
        </label>
        {groups.length > 0 ? (
          <MultiSelect
            options={[
              { value: '', label: 'None - No escalation', description: 'Do not escalate on timeout' },
              ...groups.map(group => ({
                value: group.id,
                label: group.name,
                description: group.level ? `Level ${group.level} - Higher priority group` : 'Escalation group',
              }))
            ]}
            value={selectedEscalationGroup ? [selectedEscalationGroup] : []}
            onChange={handleEscalationGroupChange}
            placeholder="Select escalation group..."
            disabled={isReadOnly}
            loading={groupsLoading}
            helpText="Group to escalate to when timeout is reached (single selection)"
          />
        ) : (
          <div>
            {groupsLoading ? (
              <div className="mt-1 p-2 text-sm text-gray-500 bg-gray-50 rounded">
                Loading groups...
              </div>
            ) : (
              <input
                type="text"
                value={selectedEscalationGroup}
                onChange={(e) => {
                  updateConfig('escalationGroup', e.target.value);
                }}
                disabled={isReadOnly}
                placeholder="Enter group ID or name for escalation"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
              />
            )}
            <p className="mt-1 text-xs text-gray-500">
              {groupsLoading ? 'Groups are being loaded...' : 'Enter group name or ID for escalation'}
            </p>
          </div>
        )}
      </div>

      {/* Configuration Summary */}
      {selectedApprovers.length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 rounded-md">
          <h4 className="text-xs font-semibold text-blue-900 mb-1">Configuration Summary:</h4>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>• Approver Type: <strong>{approverType}</strong></li>
            <li>• Selected Approvers: <strong>{selectedApprovers.length}</strong></li>
            {selectedApprovers.length <= 5 && (
              <li className="ml-4 text-xs italic">{selectedApprovers.join(', ')}</li>
            )}
            {configuration.unanimous && <li>• Requires unanimous approval</li>}
            {configuration.timeout && <li>• Timeout: <strong>{configuration.timeout} hours</strong></li>}
            {selectedEscalationGroup && <li>• Will escalate on timeout to: <strong>{selectedEscalationGroup}</strong></li>}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ApprovalConfig;